const EPS = 1e-9;
/**
 * Punkt-in-Polygon per Ray-Casting (ungerade Schnittzahl = innen).
 *
 * Randverhalten (präzise): Diese Funktion verwendet KEIN Epsilon und entscheidet
 * Punkte exakt auf einer Kante NICHT robust — das Ergebnis ist dort
 * implementierungs-/orientierungsabhängig (kann true ODER false sein) und hängt
 * von der Floating-Point-Arithmetik der Ray-Casting-Ungleichungen ab. Konkret:
 * dank der Halb-offenen Konvention `a.y > p.y !== b.y > p.y` wird der untere
 * Kantenendpunkt einer Kante mitgezählt, der obere nicht; horizontale Kanten auf
 * `p.y` werden nie gezählt. Für Rand-/Setback-Entscheidungen daher NICHT diese
 * Funktion verwenden, sondern die vorzeichenbehaftete Distanz
 * {@link signedDistanceToPolygon} (Betrag = Randabstand, Vorzeichen = innen/außen)
 * mit einer fachlich gewählten Toleranz.
 *
 * < 3 Punkte → immer false. Reihenfolge-/orientierungsunabhängig (CW = CCW).
 */
export function pointInPolygon(p, polygon) {
    const n = polygon.length;
    if (n < 3)
        return false;
    let inside = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        const straddles = a.y > p.y !== b.y > p.y;
        if (straddles && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
            inside = !inside;
        }
    }
    return inside;
}
/** Kürzeste Distanz Punkt → Strecke (a,b). */
export function distancePointToSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 <= EPS)
        return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
/** Kürzeste Distanz Punkt → Polygon-Rand (Betrag, ≥ 0). */
export function distancePointToPolygon(p, polygon) {
    const n = polygon.length;
    if (n < 2)
        return Infinity;
    let min = Infinity;
    for (let i = 0; i < n; i++) {
        const d = distancePointToSegment(p, polygon[i], polygon[(i + 1) % n]);
        if (d < min)
            min = d;
    }
    return min;
}
/** Vorzeichenbehaftete Randdistanz: innen = +, außen = −. Für Randabstands-/Setback-Tests. */
export function signedDistanceToPolygon(p, polygon) {
    const d = distancePointToPolygon(p, polygon);
    return pointInPolygon(p, polygon) ? d : -d;
}
function cross(a, b, c) {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}
function onSegment(a, b, p, eps) {
    return (Math.min(a.x, b.x) - eps <= p.x &&
        p.x <= Math.max(a.x, b.x) + eps &&
        Math.min(a.y, b.y) - eps <= p.y &&
        p.y <= Math.max(a.y, b.y) + eps);
}
/**
 * Schneiden sich die Strecken (p1,p2) und (p3,p4)? (inkl. Berührung/Kollinearität).
 * @param eps Toleranz für die Kollinearitäts-/Berührungs-Tests (Default = {@link EPS},
 *   d.h. das Standardverhalten ist unverändert). Größere Werte machen den Test
 *   toleranter gegenüber Floating-Point-Rauschen.
 */
export function segmentsIntersect(p1, p2, p3, p4, eps = EPS) {
    const d1 = cross(p3, p4, p1);
    const d2 = cross(p3, p4, p2);
    const d3 = cross(p1, p2, p3);
    const d4 = cross(p1, p2, p4);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
        return true;
    }
    if (Math.abs(d1) <= eps && onSegment(p3, p4, p1, eps))
        return true;
    if (Math.abs(d2) <= eps && onSegment(p3, p4, p2, eps))
        return true;
    if (Math.abs(d3) <= eps && onSegment(p1, p2, p3, eps))
        return true;
    if (Math.abs(d4) <= eps && onSegment(p1, p2, p4, eps))
        return true;
    return false;
}
/** Überlappen sich zwei einfache Polygone? Kantenschnitt ODER Containment (eins im anderen). */
export function polygonsIntersect(a, b) {
    if (a.length < 3 || b.length < 3)
        return false;
    for (let i = 0; i < a.length; i++) {
        const a1 = a[i];
        const a2 = a[(i + 1) % a.length];
        for (let j = 0; j < b.length; j++) {
            if (segmentsIntersect(a1, a2, b[j], b[(j + 1) % b.length]))
                return true;
        }
    }
    if (pointInPolygon(a[0], b))
        return true;
    if (pointInPolygon(b[0], a))
        return true;
    return false;
}
/**
 * Ist das Polygon "einfach" (simple), d.h. ohne Selbst-Überschneidungen?
 *
 * Liefert false, wenn das Polygon weniger als 3 Eckpunkte hat oder wenn sich
 * zwei NICHT benachbarte Kanten schneiden (inkl. Berührung/kollineare Überlappung).
 * Benachbarte Kanten teilen sich per Definition einen Eckpunkt — dieser geteilte
 * Endpunkt wird NICHT als Selbstschnitt gewertet. Die Adjazenz schließt den
 * Umlauf ein (letzte Kante ↔ erste Kante). Das Polygon wird als geschlossen
 * betrachtet (Kante n−1 → 0 wird implizit ergänzt).
 *
 * Beispiele: einfaches Quadrat → true · "Bowtie"/Sanduhr (sich kreuzende Kanten)
 * → false · < 3 Punkte → false.
 *
 * Hinweis: Ein geteilter Eckpunkt nicht-benachbarter Kanten (z.B. eine "8" mit
 * gemeinsamem Mittelpunkt) gilt als Selbstschnitt und liefert false.
 *
 * @param polygon Eckpunkte in Reihenfolge (offen; Schließkante wird ergänzt).
 * @param eps Toleranz, durchgereicht an {@link segmentsIntersect} (Default = {@link EPS}).
 */
export function isSimplePolygon(polygon, eps = EPS) {
    const n = polygon.length;
    if (n < 3)
        return false;
    // Alle Kantenpaare prüfen; benachbarte Kanten (inkl. Umlauf) überspringen.
    for (let i = 0; i < n; i++) {
        const a1 = polygon[i];
        const a2 = polygon[(i + 1) % n];
        for (let j = i + 1; j < n; j++) {
            // Kanten i und j sind benachbart, wenn sie einen Eckpunkt teilen:
            // j === i+1 (gemeinsamer Punkt i+1) oder der Umlauf i===0 && j===n−1.
            const adjacent = j === i + 1 || (i === 0 && j === n - 1);
            if (adjacent)
                continue;
            const b1 = polygon[j];
            const b2 = polygon[(j + 1) % n];
            if (segmentsIntersect(a1, a2, b1, b2, eps))
                return false;
        }
    }
    return true;
}
/** Ebene Polygonfläche (Shoelace, Betrag) in den Einheiten der Vec2-Koordinaten (m² bei Metern). */
export function polygonAreaPlanar(polygon) {
    const n = polygon.length;
    if (n < 3)
        return 0;
    let s = 0;
    for (let i = 0; i < n; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % n];
        s += a.x * b.y - b.x * a.y;
    }
    return Math.abs(s) / 2;
}
//# sourceMappingURL=polygon.js.map